import type {
  AcceptTeamInvitationResponse,
  CompleteTeamWorkspaceRequest,
  CreateTeamWorkspaceRequest,
  CreateTeamWorkspaceResponse,
  CreateTeamMemberRequest,
  CreateTeamMemberResponse,
  InviteTeamMemberRequest,
  RequestTeamJoinRequest,
  RequestTeamJoinResponse,
  Team,
  TeamEntitlementsResponse,
  TeamInvitation,
  UpdateTeamMemberRolesRequest,
  UpdateTeamRequest,
  User,
} from '@kangaroo/shared';

import { api } from '../lib/api';

interface ApiResponse<T> {
  data: T;
}

export const currentTeamKey = '/teams/current';
export const currentTeamMembersKey = '/teams/current/members';
export const currentTeamInvitationsKey = '/teams/current/invitations';
export const currentTeamEntitlementsKey = '/teams/current/entitlements';

export async function fetchCurrentTeam(): Promise<Team> {
  const response = await api.get<ApiResponse<Team>>(currentTeamKey);
  return response.data.data;
}

export async function createCurrentTeam(payload: CreateTeamWorkspaceRequest): Promise<CreateTeamWorkspaceResponse> {
  const response = await api.post<ApiResponse<CreateTeamWorkspaceResponse>>(currentTeamKey, payload);
  return response.data.data;
}

export async function completeCurrentTeamSetup(payload: CompleteTeamWorkspaceRequest): Promise<CreateTeamWorkspaceResponse> {
  const response = await api.post<ApiResponse<CreateTeamWorkspaceResponse>>('/teams/current/complete-setup', payload);
  return response.data.data;
}

export async function updateCurrentTeam(payload: UpdateTeamRequest): Promise<Team> {
  const response = await api.put<ApiResponse<Team>>(currentTeamKey, payload);
  return response.data.data;
}

export async function createTeamMember(payload: CreateTeamMemberRequest): Promise<CreateTeamMemberResponse> {
  const response = await api.post<ApiResponse<CreateTeamMemberResponse>>(`${currentTeamKey}/members`, payload);
  return response.data.data;
}

export async function fetchTeamMembers(): Promise<User[]> {
  const response = await api.get<ApiResponse<User[]>>(currentTeamMembersKey);
  return response.data.data;
}

export async function fetchTeamInvitations(): Promise<TeamInvitation[]> {
  const response = await api.get<ApiResponse<TeamInvitation[]>>(currentTeamInvitationsKey);
  return response.data.data;
}

export async function inviteTeamMember(payload: InviteTeamMemberRequest): Promise<TeamInvitation> {
  const response = await api.post<ApiResponse<TeamInvitation>>(currentTeamInvitationsKey, payload);
  return response.data.data;
}

export async function resendTeamInvitation(invitationId: string): Promise<TeamInvitation> {
  const response = await api.post<ApiResponse<TeamInvitation>>(`${currentTeamInvitationsKey}/${invitationId}/resend`);
  return response.data.data;
}

export async function requestTeamJoin(payload: RequestTeamJoinRequest): Promise<RequestTeamJoinResponse> {
  const response = await api.post<ApiResponse<RequestTeamJoinResponse>>('/teams/join-requests', payload);
  return response.data.data;
}

export async function acceptTeamInvitation(invitationId: string): Promise<AcceptTeamInvitationResponse> {
  const response = await api.post<ApiResponse<AcceptTeamInvitationResponse>>(`${currentTeamInvitationsKey}/${invitationId}/accept`);
  return response.data.data;
}

export async function fetchTeamEntitlements(): Promise<TeamEntitlementsResponse> {
  const response = await api.get<ApiResponse<TeamEntitlementsResponse>>(currentTeamEntitlementsKey);
  return response.data.data;
}

export async function updateTeamMemberAccess(userId: string, disabled: boolean): Promise<User> {
  const response = await api.patch<ApiResponse<User>>(`${currentTeamMembersKey}/${userId}/access`, { disabled });
  return response.data.data;
}

export async function updateTeamMemberRoles(userId: string, payload: UpdateTeamMemberRolesRequest): Promise<User> {
  const response = await api.patch<ApiResponse<User>>(`${currentTeamMembersKey}/${userId}/roles`, payload);
  return response.data.data;
}
